/**
 * Single source of truth for user-facing security vocabulary.
 * Maps internal technical identifiers (AAL levels etc.) to plain-language
 * labels per docs/superpowers/specs/2026-06-12-plain-language-ui-design.md.
 * Pure data + format helpers — no React, no security logic.
 */

// EVENT_TYPE_LABELS moved to shared/event-type-labels.ts (module 01 Task 5,
// AC-6) so the server search layer can reuse it — re-exported below so any
// other direct importer keeps working.
import { EVENT_TYPE_LABELS } from "@shared/event-type-labels";
import type { VaultCategoryType, VaultRowStatus } from "@/pages/vault-shared/vault-types";
import type { TenureBucket } from "@shared/lib/sender-context";
import { JARGON_PATTERN } from "./jargon-pattern";
import { levelLabel, levelOfFourLabel, levelOfFourDisplay } from "@shared/lib/security-posture/tier-ladder";
import { PRODUCT_NAME, PRODUCT_NAME_POSSESSIVE } from "@shared/brand";
export { EVENT_TYPE_LABELS };

export interface ProtectionLevelInfo {
  /** Full label, e.g. "Level 3 of 4" */
  label: string;
  /** Short form for badges, e.g. "Level 3" */
  short: string;
}

/**
 * Task 8 (AC-9): the "Basic"/"Strong"/"Maximum" protection vocabulary is
 * retired — gate copy now reads through the shared Level-N ladder formatter.
 * AAL1/AAL2/AAL3 map to Level 1/2/3 (never Level 4): the ladder's Level 4
 * additionally requires an enrolled, recovery-ready guardian on the account
 * (module 01 §"The four-level ladder", AC-66) — a fact AAL alone never
 * carried, so claiming Level 4 here would be false assurance (Friction Rule
 * clause 2). See `shared/lib/security-posture/tier-ladder/format.ts` header
 * for the full mapping rationale.
 */
export const PROTECTION_LEVELS: Record<"AAL1" | "AAL2" | "AAL3", ProtectionLevelInfo> = {
  AAL1: { label: levelOfFourLabel(1), short: levelLabel(1) },
  AAL2: { label: levelOfFourLabel(2), short: levelLabel(2) },
  AAL3: { label: levelOfFourLabel(3), short: levelLabel(3) },
};

export const VAULT_ROW_STATUS: Record<VaultRowStatus, {
  label: string;
  tone: "verified" | "warn" | "neutral";
}> = {
  verified: { label: "Verified", tone: "verified" },
  self_declared: { label: "Self-declared", tone: "warn" },
  pending: { label: "Waiting for confirmation", tone: "warn" },
  pending_quorum: { label: "Awaiting approval", tone: "warn" },
  superseded: { label: "Replaced", tone: "neutral" },
  locked: { label: `Locked by ${PRODUCT_NAME}`, tone: "warn" },
};

/** Humanized label for a vault entry's category, mirroring VAULT_ROW_STATUS.
 *  Never render `identifier.artifactType` / `row.type` directly — always
 *  look it up here so a raw snake_case enum never reaches the screen. */
export const VAULT_CATEGORY_TYPE_LABELS: Record<VaultCategoryType, string> = {
  contact: "Contact",
  digital_account: "Digital account",
  bank_account: "Bank account",
  credit_card: "Credit card",
  custom: "Custom",
  government_id: "Government ID",
  officer_key: "Signing key",
};

/** Safe lookup: unknown levels fall back to a neutral plain label so renders
 *  never throw AND never leak raw internal codes (e.g. "AAL9") on screen. */
export function protectionLevel(aal: string): ProtectionLevelInfo {
  return (
    PROTECTION_LEVELS[aal as keyof typeof PROTECTION_LEVELS] ?? {
      label: "Unknown protection",
      short: "Unknown",
    }
  );
}

const AAL_TO_LADDER_LEVEL: Record<"AAL1" | "AAL2" | "AAL3", 1 | 2 | 3> = {
  AAL1: 1,
  AAL2: 2,
  AAL3: 3,
};

/**
 * Owner ruling 2026-09-01: the hybrid "Level N of 4 · <name>" headline form
 * (`levelOfFourDisplay`), for the small set of standalone STATUS readouts
 * that state "what level is this session/account AT right now" as their own
 * line — the current-level readout in `ProtectionLevelSummary.tsx` and its
 * "every level" ladder list. Deliberately NOT what `PROTECTION_LEVELS.label`
 * itself returns: most of that constant's callers embed the plain scale
 * form mid-sentence (a gate requirement, a locked-content note, a reason
 * line), where appending a name would read as a fragment rather than a
 * headline — see the `security-tier-ladder` descriptor-names task report for
 * the full per-call-site list. Falls back to the plain scale-only label for
 * an unknown AAL so a render never throws.
 */
export function protectionLevelDisplay(aal: string): string {
  const level = AAL_TO_LADDER_LEVEL[aal as keyof typeof AAL_TO_LADDER_LEVEL];
  return level ? levelOfFourDisplay(level) : protectionLevel(aal).label;
}

/** Recurring vocabulary (each used by ≥2 components). One-off page copy stays inline. */
export const TERMS = {
  securityFreeze: "Security freeze",
  exposureAlert: "Exposure alert",
  technicalDetails: "Technical details",
} as const;

/**
 * The GENERIC 423 Forensic Pause sentence (#3386) — for a call site that has
 * no more specific freeze copy of its own and reaches `error-copy.ts`'s
 * `describeError` fallback. Matches the wording already hand-typed at
 * `StepUpGate-support.ts`'s `FORENSIC_PAUSE_ACTIVE` branch (reuse, not a
 * third variant) — built from `TERMS.securityFreeze` rather than a bare
 * literal so the noun stays one source. Deliberately never says "try
 * again": a frozen account is not a retry state (CLAUDE.md §Forensic Pause;
 * red styling stays the caller's decision — this is copy only).
 */
export const FORENSIC_PAUSE_GENERIC_MESSAGE =
  `Your account is in a ${TERMS.securityFreeze.toLowerCase()}. Your guardians' approval is needed to lift it.`;

/**
 * The ONE word for a counterparty node's KIND. Three surfaces had hand-rolled
 * the same ternary (`ConversationRow`, the composer's recipient-context line,
 * and the workflow party picker), so the vocabulary was one edit away from
 * drifting across pickers that sit next to each other.
 *
 * Says only what kind of account it is — never anything about verification,
 * standing, or who is operating it (that is `connectionStanding`, below).
 *
 * @param plural  the group-header form ("Individuals" / "Organisations").
 */
export function nodeKindLabel(nodeKind: "personal" | "org", plural = false): string {
  const word = nodeKind === "personal" ? "Individual" : "Organisation";
  return plural ? `${word}s` : word;
}

/**
 * Plain-English label for a counterparty ACCOUNT's connection standing — shown
 * on the conversation header (AC-23). HONESTY (Friction Rule clause 2): the
 * sole input is `org_connections.status === "active"` — both parties accepted
 * a connection. That is NOT an identity check (no domain/ABR/IDV runs here),
 * and it says NOTHING about whoever sent the current message — not that they
 * were present, not that a person rather than software acted. The label
 * therefore never uses "Verified"/"Unverified": those words claim a check that
 * didn't happen. The noun is the ACCOUNT ("organisation" / "individual"),
 * never "connection" or "sender" — so the copy can't read as a claim about the
 * person behind this message. Plain words only; no AAL/PRF/attestation jargon.
 *
 * ONLY the active (established) state has a label. CLAUDE.md §Entity trust
 * provenance rule (d) — show only affirmative states, never an explicit
 * "not verified/new" — applies here too: a non-active connection (pending /
 * declined / revoked) renders NO standing badge at all (`connectionStanding`
 * returns `null`) rather than a "Connection pending" negative-adjacent label.
 */
export const CONNECTION_STANDING = {
  org: {
    /** `org_connections.status === "active"` — an accepted, established org account. */
    verified: {
      label: "Connected organisation",
      hint: "You're connected with this account. That isn't an identity check, and it doesn't show who sent any message.",
    },
  },
  personal: {
    verified: {
      label: "Connected individual",
      hint: "You're connected with this account. That isn't an identity check, and it doesn't show who sent any message.",
    },
  },
} as const;

/**
 * Map a counterparty's node-kind + connection status to its plain-English
 * account-standing label (AC-23), or `null` for any non-active status —
 * the caller renders NO badge in that case (CLAUDE.md §Entity trust
 * provenance rule (d): affirmative states only). `nodeKind` picks the noun
 * (organisation / individual); `status === "active"` is the sole affirmative
 * state. Unknown node-kinds fall back to the organisation wording so a render
 * never throws.
 */
export function connectionStanding(
  nodeKind: string,
  status: string,
): { label: string; hint: string } | null {
  if (status !== "active") return null;
  return nodeKind === "personal" ? CONNECTION_STANDING.personal.verified : CONNECTION_STANDING.org.verified;
}

/**
 * User-facing vocabulary for the org "signing key" surface (formerly "officer
 * keys"). Internals — table, routes, testids, the deep-link anchor — keep the
 * byte-identical "officer" token; only visible copy uses these labels.
 * The enrolledStatus/needsSetupStatus keys are consumed by the later roster +
 * nudge work — included now so those tasks stay DRY.
 */
export const SIGNING_KEY = {
  sectionHeading: "Signing keys",
  /** Canonical route to the signing-key enrol control — the People tab, on the
   *  viewer's own row (the Governance tab this used to point at is gone). */
  setupHref: "/dashboard/people",
  setupCta: "Set up my signing key",
  /** Same ceremony, second-and-later device — a spare, not a blocking step. */
  addAnotherCta: "Add another device",
  runningState: "Waiting for your security key…",
  success: "This device is now a signing key.",
  panelIntro: "Register this device so you can approve changes for this organisation.",
  enrolledStatus: "Ready to approve",
  needsSetupStatus: "Needs to finish setup",
  /**
   * Holds a registered key but no active administrator seat. `signingAdmins`
   * needs BOTH, so this person genuinely cannot confirm a change — calling them
   * ready would overstate the org's approver count against the L0-18 floor.
   */
  notApproverStatus: "Not an approver",
  notApproverExplainer:
    "They have a registered device, but they are not an administrator of this organisation, so they cannot confirm a change.",
  /** The viewer cannot see enough to say either way — claim nothing. */
  standingUnknownStatus: "Key registered",
  standingUnknownExplainer:
    "They have registered a device. Whether they can confirm a change also depends on their role, which an administrator can check on the People tab.",

  // -------------------------------------------------------------------------
  // First-time-user copy for the Governance → Signing keys surface.
  //
  // HONESTY (Friction Rule clause 2): every string below describes POSSESSION —
  // a person confirming with a device they hold. Nothing here claims the person
  // is live, present, or not being impersonated.
  // -------------------------------------------------------------------------

  /** One-line answer to "what am I looking at?" on the Governance tab. */
  whatThisIs:
    "Signing keys are how your organisation approves changes that matter — adding a bank account, changing who is trusted, or loosening an approval rule.",
  /**
   * Why more than one person is needed. Plain English for the quorum floor.
   *
   * HONESTY (clause 2), and the reason this is worded narrowly: an earlier
   * draft said "no single account — even a compromised one — can move money or
   * change trust on its own." That is a product-wide guarantee this codebase
   * does NOT deliver — ledger #33's sub-note leaves a sole-admin org able to
   * grant a second administrator alone, L0-18-D relaxes disclosure of an
   * already-verified account to a single officer, and ledger #30(e) records the
   * Operator/Viewer boundary as unenforced. The copy therefore claims only what
   * this rule enforces, about the changes named directly above it.
   */
  whyTwoPeople:
    "Each of those changes has to be confirmed by more than one person, each on their own device — so one account acting alone cannot make them.",
  /** What a "signing key" physically is, for someone who has never set one up. */
  whatAKeyIs:
    "A signing key is a device you already have — this laptop, your phone, or a plug-in security key. Setting one up takes about a minute and asks for your fingerprint, face, or device PIN.",

  /** People roster heading — a list of PEOPLE, not credentials. */
  peopleHeading: "Who can approve changes",
  /**
   * Subtitle for the ONE person-then-devices roster on Governance. It has to
   * cover what the two former subtitles covered between them: who may be asked,
   * who may actually confirm, and that the rows beneath a person are the devices
   * they registered (more than one being a spare, not a second vote).
   */
  /**
   * REWORDED for the self-only device disclosure (owner directive 2026-08-10).
   * It previously promised "under each person are the devices they registered",
   * which is no longer true and must not be left standing: a colleague's row
   * now shows their standing and how many devices they hold, never which ones.
   * Saying so explicitly is the point — an unexplained absence reads as the
   * page having failed to load them.
   */
  rosterSubtitle:
    "Everyone below can be asked to approve. Only the ones marked ready can confirm a change. You can see how many devices each person has registered — a spare means they are not locked out if they lose the first — but which devices those are is only ever visible to them.",

  // -------------------------------------------------------------------------
  // Your own devices, reconciled against your account (self-only block).
  //
  // HONESTY (clause 2): "signing device" is a statement about an enrolment
  // record, never about anyone being present at it.
  // -------------------------------------------------------------------------

  /** Heading over the viewer's own reconciled device list. */
  myDevicesHeading: "Your devices",
  /** Explains why this list can be longer than the org's signing-key count. */
  myDevicesSubtitle:
    "Every device on your account is listed here. Only the ones marked as signing devices can approve changes for this organisation — the rest are ways you sign in.",
  /** Shown when the account's device list could not be read. Amber, not red. */
  myDevicesUnavailable:
    "We couldn't check the rest of the devices on your account, so this list may be short. Your signing devices are shown above.",

  deviceEnrolledMarker: "Signing device",
  deviceAvailableMarker: "Not a signing device",
  deviceOrphanedMarker: "No longer on your account",
  /**
   * The orphaned state. Deliberately does NOT assert that the key can no longer
   * approve — whether the server would accept a co-signature from it is the
   * server's answer, not a claim this copy is entitled to make.
   */
  deviceOrphanedExplainer:
    "This device is still registered as a signing device for this organisation, but the passkey it used is no longer on your account. Set up another device so you are not relying on it.",
  /** Fallback headline when nothing about a device was reported. */
  deviceUnnamedHeadline: "Security key",
  /** Per-device action on a device that is not yet a signing device. */
  makeSigningDeviceCta: "Make this a signing device",
  /** Devices roster heading — a list of DEVICES, so the two lists can't be confused. */
  devicesHeading: "Registered devices",
  devicesSubtitle:
    "Each device below has been registered as a signing key. One person can register more than one device — a spare means you are not locked out if you lose the first.",

  /** Shown on a person who has not registered any device yet. */
  needsSetupNextStep:
    "They need to open this page themselves and set up their own device — you cannot do it for them.",
  /** Same state, when it is the signed-in viewer. */
  needsSetupNextStepSelf: "Use the button below to set up your device now.",
} as const;

/**
 * Plain-English readiness line for the Signing keys panel — e.g.
 * "1 of the 2 people needed are ready to approve".
 *
 * HONEST about the unit (this is the bug the old copy had): the requirement is
 * a number of DISTINCT PEOPLE (L0-18's distinct-signatory floor), never a number
 * of keys — so a second device on your own account does NOT move this number,
 * and the copy must never imply it does.
 */
export function signingKeyReadiness(readyPeople: number, needed: number): string {
  return `${readyPeople} of the ${needed} people needed ${readyPeople === 1 ? "is" : "are"} ready to approve`;
}

/** "2 devices" / "1 device" / "No devices yet" — never a bare number on screen. */
export function deviceCountLabel(count: number): string {
  if (count === 0) return "No devices yet";
  return `${count} device${count === 1 ? "" : "s"}`;
}

/** "2 of 3 guardians approved" */
export function guardianApprovals(approved: number, total: number): string {
  return `${approved} of ${total} guardian${total === 1 ? "" : "s"} approved`;
}

/**
 * The People-row orphaned-key pill (owner fix round 1, module 03 finding 1).
 * A signing device whose passkey is no longer on the account is still an
 * active officer key the org counts toward who can approve -- surfacing it
 * only inside the expanded device list would bury exactly the fact someone
 * needs to see at a glance: a device they rely on to approve money is no
 * longer backed by a live passkey.
 */
export function orphanedKeyPillLabel(count: number): string {
  return count === 1 ? "A key was removed" : `${count} keys were removed`;
}

/**
 * Vetted-organisation chip tooltip (#1761 Module 02, Task 5 / AC-7). HONESTY
 * (Friction Rule clause 2, load-bearing): `vetted_at` is set by TWO different
 * paths — the four-layer + two-admin review upgrade, AND platform-invite
 * founding with `grantsVetted` (zero layers run) — so this copy MUST NOT
 * claim a specific ceremony ("completed four verification layers and a
 * two-admin review" is false for the second path). It instead NAMES both
 * routes disjunctively (organisation checks + staff review, OR a platform
 * invitation) so it is true on either path without pretending either is the
 * only one. It NEVER implies ongoing re-verification ("currently verified"),
 * and the final sentence is load-bearing and MUST stay verbatim-equivalent:
 * this is an ORGANISATION-standing fact, not a claim about who sent any
 * individual message or whether that message is true — never a claim that
 * anyone was present, and never a claim of resistance to synthetic-media
 * impersonation. `sinceLabel` is a caller-supplied date string (shared
 * `formatDateOnly`, Rule 13).
 */
export function vettedOrgTooltip(sinceLabel: string): string {
  return (
    `Vetted by ${PRODUCT_NAME} since ${sinceLabel} — either after ${PRODUCT_NAME_POSSESSIVE} organisation checks ` +
    `and a staff review, or through an invitation from ${PRODUCT_NAME}. ` +
    `It says nothing about who sent any message, or whether a message is true.`
  );
}

/**
 * Stamp assurance label vocabulary (unified-comms Slice B, epic #1290; widened
 * by messaging-experience Wave 2, ledger #20, for the window-seal stamp mode).
 *
 * A verified message stamp is HONEST about what it proves: POSSESSION (the
 * sender used their passkey) + FRESHNESS (how recently) — and NOTHING about
 * whether anyone was present. No "live", "in person", or "real person" wording
 * (Friction Rule ledger #3/#7/#8; a compromised session can still emit an
 * attested send — the stamp does NOT resist synthetic-media impersonation).
 * The level word is derived from the server `stampLabel`
 * (UV-only → "Passkey"; a memory-bearing stamp → "Passkey + memory"); the
 * full label appends a relative freshness time as `<level> · <time>`
 * (e.g. "Passkey · just now").
 *
 * The level word itself stays "Passkey" for `passkey_window` (it IS a
 * passkey-chained stamp) — the DISTINCTION lives in the composed label's
 * suffix (see stampAssuranceLabel below), never in the level word.
 *
 * Plain-English only — the JARGON_PATTERN guard (now in its own module,
 * `./jargon-pattern`) MUST still pass. Read that
 * pattern for its actual contents rather than trusting a summary here: it is a
 * TECHNICAL-JARGON screen (AAL, PRF, WebAuthn, FIDO, quorum, attestation and
 * similar), and it is NOT a general honesty gate. It does not reject "present",
 * "in person", "live" or "real person" — the repo's banned-phrase sweep over
 * these surfaces, and each surface's own rendered-text tests, are what cover
 * that. (The sweep's filename is not spelled out here on purpose: it contains a
 * banned token, and the sweep greps comments.)
 */
const STAMP_LEVEL_WORDS: Record<string, string> = {
  // UV-only possession — the W2 message stamp.
  passkey: "Passkey",
  // Possession + a fresh memory (knowledge) proof.
  passkey_questions: "Passkey + memory",
  passkey_guardians: "Passkey + guardians",
  // Window-sealed possession (messaging-experience Wave 2) — no fresh UV at
  // send time, anchored to a prior real stamp within the window.
  passkey_window: "Passkey",
};

/**
 * Plain-English LEVEL word for a stamp's contributing factors (from the server
 * `stampLabel` token). Falls back to the plainest level ("Passkey") so an
 * unknown/tampered token never leaks a raw schema token AND never over-claims.
 */
export function stampLevelWord(stampLabel: string | null | undefined): string {
  if (!stampLabel) return "Passkey";
  return STAMP_LEVEL_WORDS[stampLabel] ?? "Passkey";
}

/**
 * Compose the full HONEST stamp assurance label. `mode:'window'` composes the
 * HONEST "before send" suffix using the server-verified `anchorAgeMinutes`
 * (rounded, floor) — e.g. "Passkey · 3m before send". `mode:'direct'` (or
 * omitted) is UNCHANGED BY DEFAULT: `<level> · <time>` (e.g. "Passkey · just
 * now") — this is a "when was this sealed, relative to now" freshness display
 * (StampCell's use), which correctly changes on re-view because that IS the
 * honest answer to "how long ago".
 *
 * The suffix is driven by `mode` — a VERIFY-DERIVED value (Module 01 AC-11,
 * `StampVerifyResult.mode`), NEVER by the raw `stampLabel` string — mirroring
 * #1165's "never gate on stored label" posture, now extended to mode. A
 * tampered or absent mode (including on a `passkey_window`-labelled row)
 * cannot manufacture the "before send" claim; it falls through to the direct
 * shape. `relativeTime` is a caller-supplied plain relative-time string from
 * the shared formatter (Rule 13). The level is possession-scoped; the time /
 * anchor age is freshness — never a claim that anyone was present.
 *
 * `framing: 'before-send'` (opt-in — every existing caller is unaffected)
 * answers a DIFFERENT question: "how long before the send did the signing
 * ceremony happen" — the gap between UV assertion and seal, not "how long ago
 * was this sealed". For a direct stamp that gap is the ceremony itself
 * (~zero, by construction — the assertion IS the seal), so this framing
 * renders a CONSTANT "<level> · at send", never a viewing-relative age that
 * would silently change every time the row is reopened. `mode:'window'`
 * already answers that question honestly via `anchorAgeMinutes` and is
 * unaffected by this option (a window stamp's real, non-zero, anchor-derived
 * gap always wins over the constant framing).
 */
export function stampAssuranceLabel(
  stampLabel: string | null | undefined,
  relativeTime: string,
  mode?: "direct" | "window" | null,
  anchorAgeMinutes?: number,
  framing?: "since-now" | "before-send",
): string {
  if (mode === "window" && anchorAgeMinutes != null) {
    return `${stampLevelWord(stampLabel)} · ${anchorAgeMinutes}m before send`;
  }
  if (framing === "before-send") {
    return `${stampLevelWord(stampLabel)} · at send`;
  }
  return `${stampLevelWord(stampLabel)} · ${relativeTime.toLowerCase()}`;
}

/**
 * Plain-English device-tenure labels (ledger #69), in the STANDALONE form the
 * detail-screen `SenderContextCard` renders as its own row.
 *
 * MOVED HERE (comm-card standardisation, module 02 Task 4) from
 * `SenderContextCard.tsx`, because the assurance strip now states the same
 * fact and two copies of a coarse-bucket label map is how one of them gains a
 * new bucket and the other does not. The card and the strip import this.
 *
 * The strip words the same buckets differently — mid-sentence, after a middot
 * ("device on this account 3–12 months") rather than as a standalone row — so
 * it owns that phrasing in `assurance-model.ts`. That is a deliberate second
 * PHRASING of one fact, not a second SOURCE of it: the bucket set is here, and
 * a new bucket added to `TenureBucket` breaks both at compile time.
 *
 * Worded as an ENROLLMENT / POSSESSION fact about the SIGNING DEVICE only —
 * never a claim that anyone is present, never a location claim, never "this
 * proves it's really them" (Friction Rule clause 2).
 */
export const DEVICE_TENURE_AT_SEND_LABEL: Record<TenureBucket, string> = {
  lt_3m: "Signing device added to this account in the last 3 months",
  m3_12: "Signing device on this account for 3–12 months",
  y1_plus: "Signing device on this account for over a year",
  y2_plus: "Signing device on this account for over 2 years",
};

/**
 * Tier-specific tooltip copy for a message stamp (sender-transparency Slice 1,
 * spec module 04 §1). Keyed to the SAME server `stampLabel` tokens as
 * STAMP_LEVEL_WORDS — NOT a parallel map (Reuse-First). Describes WHICH ceremony
 * sealed the message (possession + freshness house style), NEVER ranks tiers by
 * resistance to synthetic-media impersonation and NEVER implies anyone was
 * present (Friction Rule ledger, module
 * 04 §7). `passkey_guardians` copy is defined but inert until its W6 rung ships —
 * it renders only if that value ever occurs. Falls back to the plainest tier so an
 * unknown/tampered token never over-claims.
 */
const STAMP_TIER_TOOLTIP: Record<string, string> = {
  passkey: "Sealed with a fresh passkey check at send time.",
  passkey_questions:
    "Sealed with a fresh passkey check and a memory-based check at send time.",
  passkey_guardians:
    "Sealed with a fresh passkey check and a guardian co-signature at send time.",
  // No fresh UV at THIS send — chained to an earlier real passkey check within
  // the seal window (`stamp-window-ceremony.ts`). Must not read as "fresh
  // check at send time" (B4) — that overclaims for a window-chained send.
  passkey_window:
    "Sealed within a few minutes of a passkey check — no new check at this send.",
};

/** Plain-English tier tooltip for a stamp's `stampLabel` (possession + freshness). */
export function stampTierTooltip(stampLabel: string | null | undefined): string {
  if (!stampLabel) return STAMP_TIER_TOOLTIP.passkey;
  return STAMP_TIER_TOOLTIP[stampLabel] ?? STAMP_TIER_TOOLTIP.passkey;
}

/**
 * Plain-English label for a guardian-handshake operationType, shown to an
 * approving guardian (the "what am I approving" line). Covers the trust-change
 * operations a guardian can be asked to approve, including the T8b removal/swap.
 * Falls back to safePlainLabel for any unmapped/future op so a raw snake-case
 * token never reaches the screen.
 */
const GUARDIAN_OPERATION_LABELS: Record<string, string> = {
  guardian_removal: "Remove one of their guardians",
  guardian_swap: "Replace one of their guardians",
  forensic_pause_lift: "Lift a security freeze",
  osint_reset: "Reset their security after an exposure alert",
  account_deletion: "Delete their account",
  high_value_transfer: "Approve a high-value transfer",
  device_transfer: "Move to a new device",
  account_recovery: "Recover their account",
  view_sensitive_audit: "View a sensitive security record",
  extend_session: "Stay signed in longer",
  // POSSESSION framing, not an identity claim (Friction Rule clause 2 — the
  // same rule this file states 50 lines above for device tenure). A guardian
  // vouch attests that this person is operating their own account; it never
  // establishes that anyone is present, live, or not synthetic.
  posture_vouch: "Confirm they're in control of their account",
};

/** Approver-facing plain-English label for a guardian-handshake operationType. */
export function guardianOperationLabel(operationType: string): string {
  return (
    GUARDIAN_OPERATION_LABELS[operationType] ??
    safePlainLabel(operationType, "Approve a security action")
  );
}

/** Humanize an internal snake/dot-case code ("data_breach" → "Data breach").
 *  If the result still contains technical jargon, return the fallback instead —
 *  unknown server values can never leak banned terms on screen. */
export function safePlainLabel(rawCode: string, fallback: string): string {
  const words = rawCode.replace(/[._-]/g, " ").trim();
  const label = words.charAt(0).toUpperCase() + words.slice(1);
  return label === "" || JARGON_PATTERN.test(label) ? fallback : label;
}

/**
 * Maps a raw ledger event-type token to a short plain-language label.
 * The raw token must NEVER appear in the output — it lives only in the
 * drill-in Technical-details panel.
 *
 * Known tokens get an explicit, human-reviewed label.
 * Unknown tokens are humanized via safePlainLabel (lowercase, spaces,
 * sentence case) so the raw SCREAMING_SNAKE token never reaches the UI.
 */

/** Turns a raw ledger event-type token into a short plain-language label.
 *  Never returns the raw token verbatim — unknown tokens are humanized.
 *  The fallback splits on `.`, `_`, and `-` so future `foo.bar.baz` tokens
 *  always render as "Foo bar baz", never as a dotted raw token. */
export function eventTypeLabel(eventType: string): string {
  const explicit = EVENT_TYPE_LABELS[eventType];
  if (explicit !== undefined) return explicit;
  // The cert ledger writes many event types in dotted-lowercase form that
  // parallels the SCREAMING_SNAKE registry keys (e.g. "aal.upgraded" ↔
  // "AAL_UPGRADED", "vault.seed.created" ↔ "VAULT_SEED_CREATED"). Try the
  // normalised uppercase key before humanising, so these resolve to the curated
  // label instead of tripping the jargon guard (\bAAL\b, \bseed, WebAuthn …)
  // and degrading to the bare "Event" fallback.
  const normalised = EVENT_TYPE_LABELS[eventType.toUpperCase().replace(/[.-]/g, "_")];
  if (normalised !== undefined) return normalised;
  // Lowercase then replace all separator chars (including dots) with spaces,
  // then capitalise the first letter.  Pass through safePlainLabel so any
  // inadvertent jargon falls back to "Event".
  return safePlainLabel(eventType.toLowerCase().replace(/[._-]/g, " "), "Event");
}
